<template>
  <div
    v-if="event"
  >
    <div class="uk-grid">
      <div class="uk-width-1-1">
        <UserMenu />
      </div>
      <div
        v-if="!inIframe"
        class="uk-width-1-1 uk-text-center uk-margin-top"
      >
        <h1>{{ event.data.title }}</h1>

        <p v-if="event.data.hosted_uri">
          <a
            target="_blank"
            :href="event.data.hosted_uri"
          >View full event page <span class="uk-icon-external-link" /></a>
        </p>
      </div>
      <div
        v-else
        class="uk-width-1-1 uk-text-center"
      >
        <p>
          Trouble signing in or submitting your entries? <a
            target="_blank"
            :href="event.links.event"
          >Try the onsite version</a>.
        </p>
      </div>
      <div
        v-if="event.data.show_countdowns"
        class="uk-margin-top uk-width-1-1 countdownGrid"
        :class="'countdownGrid--cols' + activeCountdowns"
      >
        <div
          v-if="showRegistrationCountdown"
          class="countdownGrid__item"
        >
          <div class="menuCountdown">
            <div class="menuCountdown__title">
              {{ event.meta.theme.countdown_registration_ends_label }}
              <span class="menuCountdown__time">{{
                formatUTCDate(event.data.deadline_submissions_utc)
              }}</span>
            </div>
            <Countdown
              :theme="event.meta.theme.countdown_theme"
              :datetime="event.data.deadline_submissions_utc"
              :finished-text="$t('events.registration_countdown_ended')"
              :css-classes="countdownClasses('deadline_submissions_utc')"
            />
          </div>
        </div>
        <div
          v-if="showReplacementCountdown"
          class="countdownGrid__item"
        >
          <div class="menuCountdown">
            <div class="menuCountdown__title">
              {{ event.meta.theme.countdown_replacement_ends_label }}
              <span class="menuCountdown__time">{{
                formatUTCDate(event.data.deadline_replace_video_utc)
              }}</span>
            </div>
            <Countdown
              :theme="event.meta.theme.countdown_theme"
              :datetime="event.data.deadline_replace_video_utc"
              :finished-text="$t('events.upload_countdown_ended')"
              css-classes="countdown--urgent"
            />
          </div>
        </div>
        <div
          v-if="showEventEndsCountdown"
          class="countdownGrid__item"
        >
          <div class="menuCountdown">
            <span class="menuCountdown__title">{{
              event.meta.theme.countdown_event_ends_label
            }} <span class="menuCountdown__time">{{
              formatUTCDate(event.data.ends)
            }}</span></span>
            <Countdown
              :theme="event.meta.theme.countdown_theme"
              :datetime="event.data.ends"
              :finished-text="$t('events.event_ends_countdown_ended')"
              :css-classes="countdownClasses('ends')"
            />
          </div>
        </div>
        <div
          v-for="custom in event.meta.custom_countdowns"
          :key="custom.title"
          class="countdownGrid__item"
        >
          <div class="menuCountdown">
            <span class="menuCountdown__title">{{
              custom.title
            }} <span class="menuCountdown__time">{{
              formatUTCDate(custom.target_utc)
            }}</span></span>
            <Countdown
              :theme="event.meta.theme.countdown_theme"
              :datetime="custom.target_utc"
              :background-color="custom.background_color"
              :finished-text="custom.ended_label || $t('events.event_ends_countdown_ended')"
            />
            <div
              v-if="custom.description"
              class="uk-margin-top"
            >
              {{ custom.description }}
            </div>
          </div>
        </div>
      </div>
    </div>


    <div
      class="uk-grid"
      data-uk-grid-match="{row: false}"
    >
      <div
        v-if="event.data.deadline_submissions_passed"
        class="uk-width-1-1"
      >
        <div
          v-if="!hasReplacementDeadline || event.data.deadline_replace_video_passed"
          class="uk-alert uk-margin-top uk-margin-bottom"
        >
          {{ $t('events.deadline_submissions_passed') }}
        </div>
        <div
          v-if="hasReplacementDeadline && !event.data.deadline_replace_video_passed"
          class="uk-alert uk-margin-top uk-margin-bottom"
        >
          {{ $t('events.deadline_submissions_passed_uploads_possible') }}
        </div>
      </div>

      <div
        v-if="entries.length > 0"
        class="uk-width-1-1"
      >
        <h1>{{ event.meta.theme.header_my_entries }}</h1>
      </div>

      <EntrySummary
        v-for="entry in entries"
        :key="entry.data.reference"
        class="uk-width-medium-1-2 uk-width-1-1 uk-margin-top"
        :event-id="event.data.id"
        :entry="entry"
        @create-entry="createEntry"
      />

      <div :class="{'uk-width-9-10 uk-width-medium-1-2': true, 'uk-container-center': entries.length === 0}">
        <RegisterButton
          v-if="!event.data.deadline_submissions_passed && userAuthenticated"
          :create="entries.length === 0"
          :text="event.meta.theme.register_event_button"
          @click="createEntry"
        />
      </div>
    </div>


    <div
      v-if="!userAuthenticated"
      class="uk-grid"
    >
      <div class="uk-width-9-10 uk-width-medium-6-10 uk-width-large-1-2 uk-container-center">
        <Authenticate />
      </div>
    </div>

    <div id="uploader_browse_files_entries" />

    <EntriesCart
      :entries="unpaidEntries"
      :event="event"
    />
  </div>
</template>

<script lang="ts">
import EntryForm from './EntryForm.vue';
import { createUploader } from '../../lib/upload';
import AnyFileHandler from '../../lib/upload/AnyFileHandler';
import EventEntryModel, { create as createEntry } from '../../models/EventEntryModel';
import EventModel from "../../models/EventModel";
import { Component, Vue, Watch } from 'vue-property-decorator';
import EntriesCart from "./EntriesCart.vue";
import EntrySummary from "./EntrySummary.vue";
import RegisterButton from './RegisterButton.vue';
import UserMenu from '../user/Menu.vue';
import Login from "../user/Login.vue";
import Authenticate from "../user/Authenticate.vue";
import Countdown from "../misc/Countdown.vue";
import { parseISO } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { format, utcToZonedTime } from "date-fns-tz";
import "../../style/events.css";

@Component({
  components: {
    Authenticate,
    EntryForm,
    EntriesCart,
    EntrySummary,
    RegisterButton,
    UserMenu,
    Login,
    Countdown,
  }
})
export default class ViewEvent extends Vue {
  uploaderInit = false;

  loadingEntries = true;

  showLogin = false;

  created(): void {
    this.loadingEntries = true;
//
    Promise.all([
      this.$store.dispatch('events/fetchEventById', parseInt(this.$route.params.id)),
      this.$store.dispatch('events/divisions/fetchEventDivisions', parseInt(this.$route.params.id)),
    ]).then(() => {
      this.loadingEntries = false;
    });
  }

  get inIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  get event(): EventModel {
    return this.$store.getters['events/byId'](parseInt(this.$route.params.id));
  }

  get entries(): EventEntryModel[] {
    return this.$store.getters['events/getEntriesByEvent'](parseInt(this.$route.params.id));
  }

  get unpaidEntries(): EventEntryModel[] {
    return this.entries.filter((a: EventEntryModel) => a.data.approved === 'unpaid');
  }

  get userAuthenticated(): boolean {
    return this.$store.getters['context/user'].isAuthenticated();
  }

  get showEventEndsCountdown(): boolean {
    return this.event.meta.theme.countdown_show_event_ends
        && this.event.data.ends !== '0000-00-00T00:00:00Z';
  }

  get showRegistrationCountdown(): boolean {
    return this.event.meta.theme.countdown_show_registration_ends
        && this.event.data.deadline_submissions_utc !== '0000-00-00T00:00:00Z';
  }

  get showReplacementCountdown(): boolean {
    return this.event.meta.theme.countdown_show_replacement_ends
        && this.event.data.deadline_replace_video_utc !== '0000-00-00T00:00:00Z';
  }

  get activeCountdowns(): number {
    let active = 0;
    if (this.showEventEndsCountdown) {
      active++;
    }
    if (this.showRegistrationCountdown) {
      active++;
    }
    if (this.showReplacementCountdown) {
      active++;
    }

    active += this.event.meta.custom_countdowns.length;

    return active;
  }

  get hasReplacementDeadline(): boolean {
    return this.event.data.deadline_replace_video_utc !== '0000-00-00T00:00:00Z' && this.event.data.deadline_submissions_utc !== this.event.data.deadline_replace_video_utc;
  }

  @Watch('userAuthenticated', {immediate: true})
  userAuthenticationChange(nV: boolean, oV: boolean): void {
    if (nV === true && oV === false) {
      this.$store.dispatch('events/fetchMyEntries', parseInt(this.$route.params.id));
      this.tryMountUploader();
    }
  }

  @Watch('event.data.title', {immediate: true})
  setTitle(v: string) {
    if (v) {
      document.title = v;
    }
  }

  tryMountUploader(): void {
    let check = setInterval(() => {
      if (this.uploaderInit) {
        clearInterval(check);
      } else {
        this.mountUploader();
      }
    }, 100);
  }

  formatUTCDate(date: string): string {
    const localDate = utcToZonedTime(date, this.event.data.timezone);
    return format(localDate, 'MMMM dd, yyyy h:mm a zzz', {
      timeZone: this.event.data.timezone,
      locale: enUS
    }).toString();
  }

  mountUploader(): void {
    const extensions = [
      'ogg',
      'mp4',
      'm4v',
      'mkv',
      'avi',
      'mov',
      'flv',
      'mpeg',
      '3gp',
      'wmv',
      'webm',
      '3gp',
      'mkv',
      'dv',
      'mpg',
      'asf',
      '3g2'
    ];

    // const mimeTypes: string[] = extensions.reduce((a: string[], b: string) => {
    //   a.push('video/' + b);
    //   return a;
    // }, []);
    if (!this.uploaderInit && document.getElementById('uploader_browse_files_entries')) {
      createUploader('entries', this.$store, [new AnyFileHandler({allowedExtensions: extensions})], {
        headers: {'Authorization': 'Bearer ' + this.$store.getters['context/apiToken']},
        filters: {
          file_check: true,
          // mime_types: mimeTypes.join(','),
          // mime_types: "video/mp4,video/mkv,video/mov"
        }
      });
      this.uploaderInit = true;
    }
  }

  countdownClasses(key: string): string {
    const ends = parseISO(this.event.data.ends ?? '');
    const submissions = parseISO(this.event.data.deadline_submissions_utc ?? '');
    if (key === 'ends') {
      return 'countdown--' + (ends < submissions ? 'urgent' : 'important');
    } else if (key === 'deadline_submissions_utc') {
      return 'countdown--' + (ends > submissions ? 'urgent' : 'important');
    }

    return '';
  }

  createEntry(): void {
    const blank = createEntry();
    blank.data.event_id = parseInt(this.$route.params.id);
    this.$store.dispatch('events/addEntry', blank);
  }
}
</script>

<style scoped>
.menuCountdown {
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: 2px;
  padding: 2px;
}
.menuCountdown__time {
  font-weight: normal;
}
.menuCountdown__title {
  font-size: 1.1em;
  font-weight: bold;
  margin-bottom: 5px;
}
.countdownGrid {
  display: flex;
  justify-content: center;
  flex-flow: row wrap;
}
.countdownGrid__item {
  width: 28%;
  margin: 1em 2%;
}
@media only screen and (max-width: 1380px) {
  .menuCountdown__title {
    text-align: center;
  }
  .menuCountdown__time {
    display: block;
  }
}
@media only screen and (max-width: 1024px) {
  .countdownGrid__item {
    width: 40%;
  }
}
@media only screen and (max-width: 680px) {
  .countdownGrid__item {
    width: 90%;
  }
  .menuCountdown {
    flex-direction: column;
    align-items: stretch;
  }
  .menuCountdown .countdown .countdown__block {
    flex: 1 1 25%;
  }
}
</style>
